import { useEffect, useState } from 'react';
import type { LedgerAggregate } from '@fantasy/types';
import { api, ApiRequestError } from '../api.ts';

function formatRate(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'insufficient-data' }
  | { status: 'ok'; aggregate: LedgerAggregate };

interface AggregateSummaryProps {
  aggregate: LedgerAggregate;
}

/** Counts first, rate second, exactly `AdviceLedger`'s own convention — but
 * this copy is written for a stranger, not a manager reading their own
 * history, and it says the uncomfortable thing out loud rather than leaving
 * a below-half number to speak for itself: see the acceptance criteria on
 * T-094. Nothing here rounds, crops or re-baselines to flatter — a 0-total
 * transfer count reads as "no transfers recommended yet", not as a 0%
 * failure rate it never was. */
function AggregateSummary({ aggregate }: AggregateSummaryProps) {
  const captainBelowHalf = aggregate.captain.total > 0 && aggregate.captain.rate < 0.5;
  const transferBelowHalf = aggregate.transfers.total > 0 && aggregate.transfers.rate < 0.5;

  return (
    <div className="ledger-aggregate">
      <p className="hint">
        Scored across {aggregate.gameweeksScored} gameweek{aggregate.gameweeksScored === 1 ? '' : 's'}, every
        manager who has used this tool combined — not one manager's history, everyone's.
      </p>

      <dl className="ledger-stats">
        <div>
          <dt>Captain calls</dt>
          <dd>
            {aggregate.captain.beatOwnCount} of {aggregate.captain.total} beat the manager&rsquo;s own pick (
            {formatRate(aggregate.captain.rate)}){captainBelowHalf && <> — that is below half.</>}
          </dd>
        </div>
        <div>
          <dt>Transfers</dt>
          <dd>
            {aggregate.transfers.total === 0
              ? 'No transfers recommended across any scored gameweek yet.'
              : `${aggregate.transfers.gainedCount} of ${aggregate.transfers.total} gained points over the player dropped (${formatRate(aggregate.transfers.rate)})`}
            {transferBelowHalf && <> — that is below half.</>}
          </dd>
        </div>
      </dl>

      <p className="hint">
        This is every recommendation this tool has made and had scored, wins and losses alike — nothing
        here is filtered down to the calls that worked. A "beat" is measured against the alternative it
        was compared to at the time, not against the best possible outcome in hindsight, and it says
        nothing about a manager's whole season, which depends on far more than any one call.
      </p>
    </div>
  );
}

/**
 * The public honesty ledger (T-094) — the aggregate `AdviceLedger` shows a
 * signed-in manager for their own history, published instead across every
 * manager, reachable by no one at all (see `recordPublicRoute.tsx` and
 * `App.tsx`'s signed-out exception for this path, the same one `/fixtures`
 * and `/try` already have). The persuasive thing here isn't the rate — it's
 * that a rate exists and gets published at all, sample size attached, on a
 * product that could simply have not kept score. `computePublicLedgerAggregate`
 * on the server decides the below-threshold state; this component only
 * renders whichever of the two it's handed, and treats the "not enough data
 * yet" state as the one most first-time visitors will actually see.
 */
export function PublicRecord() {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    api
      .getPublicLedger()
      .then((result) => {
        if (cancelled) return;
        setState(result.status === 'ok' ? { status: 'ok', aggregate: result.aggregate } : { status: 'insufficient-data' });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: err instanceof ApiRequestError ? err.message : 'Could not load the public record.',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="panel panel--accent-2 rise-in" aria-labelledby="public-record-heading">
      <div className="panel-head">
        <div>
          <h2 id="public-record-heading">The Public Record</h2>
          <p className="hint">
            Every captain call and transfer this tool has recommended, scored once its gameweek is
            played — across every manager who has used it, not just one. No account needed to read it.
          </p>
        </div>
      </div>

      {state.status === 'loading' && (
        <p className="hint" aria-live="polite">
          Loading the record…
        </p>
      )}

      {state.status === 'error' && (
        <ul className="notices notices--flush">
          <li className="error">{state.message}</li>
        </ul>
      )}

      {state.status === 'insufficient-data' && (
        <div aria-live="polite">
          <h3>Not enough scored gameweeks yet</h3>
          <p>
            This tool records every captain call and transfer it recommends the moment it&rsquo;s given —
            before anyone knows whether it will pay off — and scores each one honestly once its
            gameweek is finished. There aren&rsquo;t yet enough scored recommendations, across every
            manager who has used this tool, to publish a rate that would mean anything: a handful of
            results is noise, not a track record, and this page won&rsquo;t dress it up as one.
          </p>
          <p className="hint">
            Check back as more gameweeks are played. Once there&rsquo;s enough data, this page will
            show the rate exactly as it lands — including if it&rsquo;s below half.
          </p>
        </div>
      )}

      {state.status === 'ok' && <AggregateSummary aggregate={state.aggregate} />}
    </section>
  );
}
